<template>
  <div
    :class="{
      'w-10 h-10 flex items-center justify-center btn-gray bg-transparent flex-nowrap hover:bg-hover-onHover':
        isListCard,
      'w-6 h-6 flex items-center justify-center box-border border border-solid border-important-border rounded':
        quickCardId && !hasNullValue,
    }"
    @mousemove="mouseMoveHandler"
    @click.stop.prevent="toggleDropdown"
  >
    <div
      v-if="!displayCardPopover"
      class="flex bg-transparent items-stretch flex-nowrap justify-start"
      :class="{
        'hover:bg-hover-onHover': isListCard,
      }"
    >
      <div class="footer-icon-wrap">
        <em
          :style="{
            ...getCustomIconStyle(iconPath(iconName)),
            backgroundColor: iconColor,
          }"
          class="custom-icons small-card-info-icons"
          :class="{ 'bg-inactive-icon': disabled && !isPriority }"
        />
        <span
          v-if="keyword === 'estimate' && !hasNullValue"
          class="total-count custom-section-value"
        >
          {{ selectedOption.value }}
        </span>
      </div>
    </div>
    <v-popover
      v-else
      ref="popover"
      interactive
      tag="div"
      content-tag="div"
      class="flex justify-center items-center"
      placement="bottom-start"
      :append-to="bodyRef"
      :role="'poper'"
      :trigger="'click'"
      :content-class="'-ml-1 popover-card-dropdown'"
      :offset="popoverOffset"
      :show-on-create="false"
      :on-trigger="onTrigger"
      :on-untrigger="onTrigger"
      :on-show="() => Boolean(items.length)"
      @state="popoverStateChange"
    >
      <v-popover
        class="flex justify-center items-center"
        :on-show="() => !isMobile"
        placement="bottom"
      >
        <div
          class="flex bg-transparent items-center flex-nowrap justify-center"
          :class="{
            'hover:bg-hover-onHover': isListCard,
            'w-6 h-6 flex items-center justify-center box-border border border-solid border-important-border rounded':
              quickCardId && hasNullValue,
          }"
        >
          <div
            class="footer-icon-wrap"
            :class="{ 'flex items-center justify-center h-6': quickCardId }"
          >
            <em
              v-if="!(keyword === 'estimate' && !hasNullValue && isQuickCardModal)"
              :style="{
                ...getCustomIconStyle(iconPath(iconName)),
                backgroundColor: iconColor,
              }"
              :class="{
                'w-4 h-4 text-[16px]': quickCardId,
                'bg-inactive-icon': disabled && !isPriority,
              }"
              class="custom-icons small-card-info-icons"
            />
            <span
              v-if="keyword === 'estimate' && !hasNullValue"
              class="total-count custom-section-value"
            >
              {{ selectedOption.value }}
            </span>
          </div>
        </div>
        <template #content>
          <div class="flex justify-center items-center">
            <em
              :style="{
                ...getCustomIconStyle(iconPath(iconName)),
                backgroundColor: iconColor,
              }"
              :class="{ 'bg-attention-icon': hasNullValue || Object.keys(defaultIcon).length }"
              class="custom-icons small-card-info-icons flex mr-1"
            />
            {{ tooltipText }}
          </div>
        </template>
      </v-popover>
      <template #content="{ state, hide }">
        <ListDropdown
          v-if="state.isVisible"
          :position="'relative'"
          :selected-item="selectedMenuItem"
          :items="items"
          :width="150"
          @click="handleDropdownClick($event, hide)"
          @closeEscape="hide"
        />
      </template>
    </v-popover>
  </div>
</template>

<script>
import ListDropdown from '@/components/widgets/ListDropdown';
import customIconsMixin from '@/mixins/customIconsMixin';
import boardListCardMixin from '@/mixins/boardListCardMixin';
import { groupByOption } from '@/types/groupByOption';
import { nanoid } from 'nanoid';
import { getSprintKey } from '@/utilities/sprintKeys';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'CardCustomSection',
  components: {
    ListDropdown,
  },
  mixins: [customIconsMixin, boardListCardMixin],

  props: {
    keyword: {
      type: String,
      required: true,
      default: '',
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    defaultIcon: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    nullValueTooltipLabel: {
      type: String,
      required: false,
      default: '',
    },
    defaultTooltipLabel: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    card: {
      type: Object,
      required: true,
    },
    isListCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    cardHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    quickCardId: {
      type: String,
      required: false,
      default: null,
    },
    listId: {
      type: [String, Number],
      required: false,
      default: '',
    },
    isQuickCardModal: {
      type: Boolean,
      required: false,
      default: true,
    },

    popoverOffset: {
      type: Array,
      required: false,
      default: () => [0, 0],
    },
  },

  emits: ['dropdownStateChange', 'hideCustomSectionDropdown'],

  setup() {
    const { isGuestReader } = useCurrentRole();
    return {
      bodyRef: document.body,
      isGuestReader,
    };
  },

  data() {
    return {
      selectedMenuItem: null,
      displayCardPopover: false,
      displayCardPopoverTimeout: null,
      dropdownOpened: false,
      uniqueId: nanoid(6),
      disableStateEmitter: false,
      showPopupTimeout: null,
    };
  },

  computed: {
    iconName() {
      return this.defaultIcon.src || this.selectedOption.src || '';
    },
    iconColor() {
      if (this.isPriority) return this.selectedOption.color;
      return !this.disabled ? this.defaultIcon.color || this.selectedOption.color : '';
    },
    tooltipText() {
      const isEstimate = this.keyword === 'estimate';
      if (isEstimate) {
        if (this.disabled) {
          return this.translate('cardSpaceEstimationTurnedOff');
        }
        if (!this.selectedOption.value) {
          return this.translate('setEstimate');
        }
      }

      // for estimate add selected abbreviation for selected system after the number of points
      const estimateSystemName =
        this.$store.getters.getEstimateSystemForProject(this.card.project_id) || '';
      const { type } = this.$constants.estimateSystems[estimateSystemName] || {};
      let { label = '' } = this.selectedOption;
      if (isEstimate && type === 'points' && label) {
        const [numOfPoints, ...rest] = label.split(' ');
        label = `${numOfPoints} ${estimateSystemName} ${rest.join(' ')}`;
      }

      const partBasedOnValue = `${label}` || this.nullValueTooltipLabel;
      const partBasedOnSection = !this.hasNullValue
        ? this.translate(this.defaultTooltipLabel || this.keyword).toLowerCase()
        : '';
      return `${partBasedOnValue} ${partBasedOnSection}`;
    },
    hasNullValue() {
      return !this.card[this.keyword];
    },
    selectedOption() {
      const selectedItem =
        this.items.find((item) => item.id === (this.card?.[this.keyword] || 0)) || {};
      const { icon: src, customIconColor: color, label, id, value } = selectedItem;
      return { src, color, label, id, value };
    },

    isPriority() {
      return this.keyword === groupByOption.priority;
    },
  },

  mounted() {
    if (this.quickCardId && !this.isGuestReader) {
      this.displayCardPopover = true;
    }
    this.handleSelectedMenuItemChange(
      this.items.findIndex((item) => item.id === (this.card?.[this.keyword] || 0))
    );
  },

  beforeUnmount() {
    if (this.displayCardPopoverTimeout) {
      clearTimeout(this.displayCardPopoverTimeout);
    }
    if (this.showPopupTimeout) {
      clearTimeout(this.showPopupTimeout);
    }
  },

  methods: {
    onTrigger(i, e) {
      e.stopPropagation();
      e.preventDefault();
    },
    popoverStateChange(state) {
      this.dropdownOpened = state.isVisible;
      if (!this.disableStateEmitter) {
        this.$emit('dropdownStateChange', state.isVisible);
      }
      if (state.isVisible) {
        this.hideDropdowns();
      }
    },
    handleDropdownClick(item, hideFn) {
      if (typeof hideFn === 'function') hideFn();
      const itemIndex = this.items.findIndex((i) => i.id === item.id);
      if (this.quickCardId) {
        this.$store.dispatch('setQuickCardField', {
          fieldName: [this.keyword],
          value: item.id,
          isModal: this.isQuickCardModal,
        });
        this.handleSelectedMenuItemChange(itemIndex);
        return;
      }

      const cardId = this.card.id;
      const oldValue = this.card[this.keyword] || 0;

      if (oldValue === item.id) {
        return;
      }

      const oldSelectedMenuItem = this.selectedMenuItem;
      this.$store.dispatch('localUpdateCardField', {
        cardId,
        fieldName: this.keyword,
        fieldValue: item.id,
      });
      if (this.keyword === groupByOption.priority) {
        this.updateGroupByOrder(item.id, oldValue);
      }
      this.$store
        .dispatch('updateCard', {
          cardId,
          card: { [this.keyword]: item.id },
          type: this.card.type,
        })
        .catch(() => {
          this.$store.dispatch('localUpdateCardField', {
            cardId,
            fieldName: this.keyword,
            fieldValue: oldValue,
          });
          if (this.keyword === groupByOption.priority) {
            this.updateGroupByOrder(oldValue, item.id);
          }
          this.handleSelectedMenuItemChange(oldSelectedMenuItem);
        });
      this.handleSelectedMenuItemChange(itemIndex);
    },
    updateGroupByOrder(newListId, oldListId) {
      const boardId = this.card.sprint_id
        ? getSprintKey(this.card.project_id, this.card.sprint_id)
        : this.card.board_id;
      this.$store.dispatch('addCardToGroupByList', {
        boardId,
        cardId: this.card.id,
        listId: newListId,
        groupBy: groupByOption.priority,
      });
      this.$store.dispatch('removeCardFromGroupByList', {
        boardId,
        cardId: this.card.id,
        listId: oldListId,
        groupBy: groupByOption.priority,
      });
    },
    hideDropdowns() {
      this.hideEditMode();
      this.hideCardActionsDropdown();
      this.hideMemberDropdown();
    },
    toggleDropdown(e) {
      if (this.disabled) {
        e.stopPropagation();
        return;
      }
      if (!this.quickCardId) {
        e.stopPropagation();
      }

      if (!this.displayCardPopover) {
        this.displayCardPopover = true;
        this.disableStateEmitter = true;
      }
      if (!this.dropdownOpened) {
        requestAnimationFrame(() => {
          this.$nextTick(() => {
            this.$refs.popover?.show?.();
          });
          this.showPopupTimeout = setTimeout(() => {
            this.$refs.popover?.show?.();
            this.disableStateEmitter = false;
            this.showPopupTimeout = null;
          }, 150);
        });
      } else {
        this.$refs.popover?.hide?.();
      }
    },
    handleSelectedMenuItemChange(index) {
      this.selectedMenuItem = index;
    },
    setCardPopover() {
      if (this.displayCardPopover) return;
      this.displayCardPopoverTimeout = setTimeout(() => {
        this.displayCardPopover = true;
      }, 30);
    },
    removePopover() {
      clearTimeout(this.displayCardPopoverTimeout);
      this.displayCardPopover = false;
    },
    mouseMoveHandler() {
      if (this.quickCardId || this.isGuestReader) return;
      this.setCardPopover();
    },
    mouseLeaveHandler() {
      if (this.quickCardId || this.dropdownOpened) return;
      this.removePopover();
    },
  },
};
</script>

<style lang="scss">
.popover-card-dropdown {
  .row {
    .item-row {
      &.selected-item {
        background: unset;

        &.highlighted-item {
          @apply bg-important-hover;
        }
      }
    }
  }
}
</style>
